import { COUNTRIES } from "../../core/constants/constants";
import { CartModel, TicketModel } from "../../core/models/cart.model";
import { getCheckoutConfig } from "../../core/models/checkout-config.model";
import { getEnvModel } from "../../core/models/env.model";
import { getGlobalConfig } from "../../core/models/global-config.model";
import {
  UserBookingDetailsModel,
  UserDetails
} from "../../core/models/user-booking-details.model";
import DataService from "../../core/services/data.service";
import LocalStorageService from "../../core/services/local-storage.service";
import RememberMeService from "../../core/services/remember-me.service";

const config = getGlobalConfig();
const env = getEnvModel();
const checkout = getCheckoutConfig();

export class CheckoutForm {
  private readonly localStorageService: LocalStorageService = new LocalStorageService();
  private readonly rememberMeService: RememberMeService;
  
  private readonly bookingAddressFields: KnockoutObservable<any>;
  private readonly bookingPersonDetails: KnockoutObservable<{
    bookingFirstName: KnockoutObservable<string | undefined>;
    bookingLastName: KnockoutObservable<string | undefined>;
    bookingEmail: KnockoutObservable<string | undefined>;
    bookingCountry: KnockoutObservable<any>;
  }>;


  // Name
  public readonly bookingFirstName: KnockoutObservable<string | undefined>;
  public readonly bookingLastName: KnockoutObservable<string | undefined>;

  // Email
  public readonly bookingEmail: KnockoutObservable<string | undefined>;

  // Phone
  private readonly isCollectPhone = ko.observable(false);
    public readonly showPhone: KnockoutComputed<boolean>;
    public readonly phoneNumberPrepend: KnockoutObservable<string>;
  public readonly bookingPhone: KnockoutObservable<string | undefined>;

  // Address
  private readonly isCollectAddress = ko.observable(false);
  public readonly showAddress: KnockoutComputed<boolean>;
  public readonly showAddress2: KnockoutComputed<boolean>;
  public readonly showState: KnockoutComputed<boolean>;

  public readonly bookingAddress: KnockoutObservable<string | undefined>;

  public readonly address2Label: string;
  public readonly bookingAddress2 = ko.observable("");

  public readonly cityLabel: string;
  public readonly bookingCity = ko.observable("");

  private readonly collectState = ko.observable(false);
  public readonly stateLabel: string;
  public readonly bookingState: KnockoutObservable<string | undefined>;
  public readonly stateRowClass: KnockoutComputed<string>;

  public readonly postcodeLabel: string;
  public readonly bookingPostcode: KnockoutObservable<string | undefined>;
  public readonly postCodePattern = ko.observable<string | undefined>("");
  public readonly maxPostCodeLength = ko.observable<number | undefined>(0);

  public readonly selectedCountryCode = ko.observable<string | undefined>("");
  private readonly bookingCountry: KnockoutObservable<any>; // TO DO: Country Model

  // Other
  public readonly boxOfficeQuickSaleEnabled = ko.observable(false);
  public readonly hasPermissionToContact?: KnockoutObservable<boolean | undefined>;
  public readonly showReceiveUpdatesTickbox: KnockoutObservable<boolean>;
  public readonly rememberMyDetails: KnockoutObservable<boolean>;

  constructor(private readonly dataService: DataService, private readonly setErrorMessage: (error: string) => void) {
    this.rememberMeService = new RememberMeService(env);

    this.address2Label = config.uiStrings.address2Label;
    this.cityLabel = config.uiStrings.cityLabel;
    this.stateLabel = config.uiStrings.stateLabel;
    this.postcodeLabel = config.uiStrings.postCodeLabel;
    this.collectState = ko.observable(config.uiStrings.collectState);
    this.showReceiveUpdatesTickbox = ko.observable(config.uiStrings.receiveUpdatesTickboxEnabled);

    if (this.showReceiveUpdatesTickbox && checkout.permissionToSendPromotionalEmails) {
      this.hasPermissionToContact = ko.observable(false);
    } else {
      this.hasPermissionToContact = ko.observable(null);
    }

    this.rememberMyDetails = ko.observable(this.rememberMeService.getRememberMeState());

    this.showPhone = ko.pureComputed(() => checkout?.collectPhoneNumberIsMandatory || this.isCollectPhone());
    this.showAddress = ko.pureComputed(() => checkout?.collectAddressIsMandatory || this.isCollectAddress());
    this.showAddress2 = ko.pureComputed(() => this.showAddress() && config.uiStrings.collectAddress2);
    this.showState = ko.pureComputed(() => this.bookingCountry() && !this.bookingCountry().IsStateHidden && this.showAddress() && this.collectState());
    this.stateRowClass = ko.pureComputed<string>(() => this.showState() ? "col-lg-6" : "col-lg-12");
    
    const patterns = {
        name: "^[a-zA-Z\\u00C0-\\u00D6\\u00D8-\\u00F6\\u00F8-\\u024F\\u1E02-\\u1E6B-'.\\u0060\\u0027\\u2019\\u2018\\s]+$",
        phone: "^\\s*[0-9][0-9\\s]*$",
        phonePrefix: "^[+]?[0-9]{1,3}$",
      email: "^[^\\s,]+[^\\s,]+\\.[^\\s,]{2,}$",
      postcode: this.postCodePattern,
    };
    this.bookingFirstName = ko.observable<string | undefined>("").extend({
      required: {
        message: "First name is required.",
        params: true,
      },
      pattern: {
        message: "First name contains invalid characters.",
        params: patterns.name,
      },
    });

    this.bookingLastName = ko.observable<string | undefined>("").extend({
      required: {
        message: "Last name is required.",
        params: true,
      },
      pattern: {
        message: "Last name contains invalid characters.",
        params: patterns.name,
      },
    });

    this.bookingPhone = ko.observable<string | undefined>("").extend({
      required: {
        params: true,
        message: "Phone number is required.",
        onlyIf: () => !this.boxOfficeQuickSaleEnabled(),
      },
      pattern: {
        message: "Phone number is invalid.",
        params: patterns.phone,
      },
    });

      this.phoneNumberPrepend = ko.observable<string>("").extend({
          pattern: {
              message: "Phone prefix is invalid.",
              params: patterns.phonePrefix,
          },
          required: {
              params: true,
              message: "Phone prefix is required and should start with a + symbol.",
              onlyIf: () => !this.boxOfficeQuickSaleEnabled(),
          },
      });

    this.bookingCountry = ko.observable().extend({
      required: {
        message: "Country is required.",
        params: true,
      },
    });

    this.bookingAddress = ko.observable<string | undefined>("").extend({
      required: {
        params: true,
        message: "Address is required.",
        onlyIf: () => !this.boxOfficeQuickSaleEnabled(),
      },
    });

    this.bookingState = ko.observable<string | undefined>("").extend({
      required: {
        params: true,
        message: config.uiStrings.stateLabel + " is required.",
        onlyIf: () => !this.boxOfficeQuickSaleEnabled(),
      },
    });

    this.bookingPostcode = ko.observable<string | undefined>("").extend({
      required: {
        params: true,
        message: config.uiStrings.postCodeLabel + " is required.",
        onlyIf: () => !this.boxOfficeQuickSaleEnabled(),
      },
      pattern: {
        message: "Postcode is invalid.",
        params: patterns.postcode,
      },
    });

    this.bookingEmail = ko.observable<string | undefined>("").extend({
      required: {
        message: "Email address is required.",
        onlyIf: () => !this.boxOfficeQuickSaleEnabled(),
      },
      emailPattern: true,
    });

    this.bookingPersonDetails = ko.validatedObservable({
      bookingFirstName: this.bookingFirstName,
      bookingLastName: this.bookingLastName,
      bookingEmail: this.bookingEmail,
      bookingCountry: this.bookingCountry,
    });

    this.bookingAddressFields = ko.validatedObservable({
      bookingAddress: this.bookingAddress,
      bookingPostcode: this.bookingPostcode,
    });

    this.boxOfficeQuickSaleEnabled.subscribe((value: boolean) => {
      if (value) {
        this.bookingFirstName("Quicksale");
        this.bookingLastName("Quicksale");
        this.bookingPhone("");
        this.bookingAddress("");
        this.bookingCity("");
        this.bookingPostcode("");
        this.bookingState("");
        this.bookingEmail("");
        this.rememberMyDetails(false);
      } else {
        this.bookingFirstName("");
        this.bookingLastName("");
        this.ResetBookingFormValidation();
      }
    });

    this.selectedCountryCode.subscribe((code: string | undefined) => {
      const country = COUNTRIES.find((v: any) => v.Code === code);

        this.CountryChanged(country);
        if (this.phoneNumberPrepend() === '') {
      this.phoneNumberPrepend(country?.CountryCode ?? '');
        }
      this.maxPostCodeLength(country?.MaxPostCodeLength);
      this.postCodePattern(country?.PostCodeFormat);

      const postcode = this.bookingPostcode();
      if (postcode  && country?.MaxPostCodeLength && country.MaxPostCodeLength < postcode.length
      ) {
        this.bookingPostcode("");
        this.bookingPostcode.isModified(false);
      }

      this.bookingCountry(country);
    });

    this.GetUserDetailsSuccessCallback(this.rememberMeService.getUserDetails());
  }

  public isValid() {
    const isValidBookingPersonDetails = this.bookingPersonDetails.isValid();

    const isValidState =
      this.bookingCountry() &&
      (!this.collectState() ||
      this.bookingCountry().IsStateHidden ||
      !this.showAddress()
        ? true
        : this.bookingState.isValid());
    const isValidAddress =
      !this.showAddress() ||
      (this.bookingAddressFields &&
        this.bookingAddressFields.isValid());
      const isValidPhoneNumber =
          !this.showPhone() || (this.bookingPhone.isValid() && this.phoneNumberPrepend.isValid());

    return isValidBookingPersonDetails && isValidState && isValidAddress && isValidPhoneNumber;
  }

  public triggerValidation() {
    this.bookingFirstName.isModified(true);
    this.bookingLastName.isModified(true);
      this.bookingPhone.isModified(true);
      this.phoneNumberPrepend.isModified(true);
    this.bookingAddress.isModified(true);
    this.bookingPostcode.isModified(true);
    this.bookingEmail.isModified(true);

    if (!this.isValid()) {
      this.setErrorMessage('One or more fields have invalid values. Please check and try again.');
    }
  }

  public triggerEmailValidation() {
    this.bookingEmail.isModified(true);

    if (!this.bookingEmail.isValid()) {
      this.setErrorMessage('One or more fields have invalid values. Please check and try again.');
    }
  }

  public getUserDetails(): UserBookingDetailsModel {
    return {
      firstName: this.bookingFirstName(),
      lastName: this.bookingLastName(),
        emailAddress: this.bookingEmail(),
        phoneNumber: this.showPhone() ? this.bookingPhone() : "",
        phonePrefix: this.showPhone() ? this.phoneNumberPrepend(): "",
      address: this.showAddress() ? this.bookingAddress() : "",
      address2: this.showAddress() ? this.bookingAddress2() : "",
      postCode: this.showAddress() ? this.bookingPostcode() : "",
      state: this.showAddress() ? this.bookingState() : "",
      countryCode: this.bookingCountry().Code,
      city: this.showAddress() ? this.bookingCity() : "",
      isQuickSale: this.boxOfficeQuickSaleEnabled(),
      browserTimezone: this.dataService.GetBrowserTimezone(),
    };
  }

  public rememberUserDetails(userDetails: UserBookingDetailsModel, accountId: number) {
    this.rememberMeService.persistRememberMeDetails(this.rememberMyDetails(), userDetails);
    this.localStorageService.SaveFavouriteEventOrganisers(accountId.toString());
  }

  public postcodeChanged() {
    const postcode = this.bookingPostcode();
    if (postcode && this.bookingCountry() && this.bookingCountry().StateList) {
      const state = this.GetStateFromPostCode(
        postcode,
        this.bookingCountry().StateList
      );
      this.bookingState(state);
    }
  }

  public updatePhoneVisibility(shoppingCartData: CartModel) {
    if (shoppingCartData.giftCertificates?.length > 0) {
      this.isCollectPhone(true);
      this.isCollectAddress(true);
    } else if (
      shoppingCartData.tickets !== null &&
      shoppingCartData.tickets.length > 0
    ) {
      this.isCollectPhone(shoppingCartData.tickets.some((p: TicketModel) => p.event.collectPhone));
      this.isCollectAddress(shoppingCartData.tickets.some((p: TicketModel) => p.event.collectAddress));
    }
  }
  private ResetBookingFormValidation() {
    setTimeout(() => {
      this.bookingFirstName.isModified(false);
      this.bookingLastName.isModified(false);
        this.bookingPhone.isModified(false);
        this.phoneNumberPrepend.isModified(false);
      this.bookingAddress.isModified(false);
      this.bookingPostcode.isModified(false);
      this.bookingEmail.isModified(false);
    });
  }

  private GetUserDetailsSuccessCallback(user: UserDetails | null) {
    if (user) {
      this.bookingFirstName(user.firstName);
      this.bookingLastName(user.lastName);
      this.bookingEmail(user.emailAddress);
        this.bookingPhone(user.phoneNumber);
        // Phone prefix may not exist so let it default to country code of returning users. 
        if (user.phonePrefix) {
        this.phoneNumberPrepend(user.phonePrefix);
        }
      this.bookingAddress(user.address);
      this.bookingPostcode(user.postCode);
      this.bookingCity(user.city);
      this.selectedCountryCode(user.countryCode);
      this.bookingState(user.state);
      this.ResetBookingFormValidation();
    } else {
      this.selectedCountryCode(config.uiStrings.isoRegionCode);
    }
  }

  private CountryChanged(data: any) {
    if (!data.IsStateHidden) {
      this.bookingState(undefined);
      this.bookingState.isModified(false);
    } else {
      this.postcodeChanged();
    }
  }

  // TO DO: MOVE TO A HELPER CLASS
  private GetStateFromPostCode(postcode: any, stateList: any): string  | undefined {
    let result = undefined;

    if (stateList && postcode) {
      for (var i = 0; i < stateList.length && !result; i++) {
        if (stateList[i].PostCodeRanges) {
          for (
            var j = 0;
            j < stateList[i].PostCodeRanges.length && !result;
            j++
          ) {
            if (
              postcode >= stateList[i].PostCodeRanges[j][0] &&
              postcode <= stateList[i].PostCodeRanges[j][1]
            ) {
              result = stateList[i].Code;
            }
          }
        }
      }
    }

    return result;
  }
}
